import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';

export const Route = createFileRoute('/admin/users/$userId')({
  component: AdminUserDetail,
});

type TierGrant = {
  id: string;
  tierId: string;
  tierName: string;
  propertyCap: number;
  source: string;
  startsAt: string;
  endsAt: string | null;
};

type UserDetail = {
  id: string;
  email: string;
  displayName: string;
  emailStatus: string;
  deletedAt: string | null;
  createdAt: string;
  tierGrant: TierGrant | null;
  properties: { id: string; name: string; role: string; archivedAt: string | null }[];
};

type Tier = { id: string; displayName: string; propertyCap: number; annualPriceCents: number };

function TierSection({ userId, tierGrant, onUpdate }: {
  userId: string;
  tierGrant: TierGrant | null;
  onUpdate: (updated: TierGrant | null) => void;
}) {
  const [selectedTierId, setSelectedTierId] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: tiersData } = useQuery({
    queryKey: ['admin-tiers'],
    queryFn: () => api.get<{ tiers: Tier[] }>('/v1/admin/tiers').then((r) => r.tiers),
  });

  const grantMutation = useMutation({
    mutationFn: (body: { tierId: string; endsAt?: string }) =>
      api.post<{ tierGrant: TierGrant }>(`/v1/admin/users/${userId}/tier-grants`, body)
        .then((r) => r.tierGrant),
    onSuccess: (grant) => {
      onUpdate(grant);
      setShowForm(false);
      setSelectedTierId('');
      setEndsAt('');
      setError(null);
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Something went wrong.');
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (grantId: string) =>
      api.delete<void>(`/v1/admin/users/${userId}/tier-grants/${grantId}`),
    onSuccess: () => onUpdate(null),
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Something went wrong.');
    },
  });

  function handleGrant(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!selectedTierId) return;
    grantMutation.mutate({ tierId: selectedTierId, ...(endsAt ? { endsAt } : {}) });
  }

  const tiers = tiersData ?? [];

  return (
    <div className="rounded-lg border border-border p-5 mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-medium">Tier / Plan</h2>
        {!showForm && (
          <Button size="sm" variant="outline" onClick={() => setShowForm(true)}>
            {tierGrant ? 'Change tier' : 'Grant tier'}
          </Button>
        )}
      </div>

      {tierGrant ? (
        <div className="flex items-center justify-between">
          <div>
            <span className="font-medium">{tierGrant.tierName}</span>
            <span className="text-xs text-muted-foreground ml-2">
              {tierGrant.propertyCap} properties · {tierGrant.source}
            </span>
            {tierGrant.endsAt && (
              <p className="text-xs text-muted-foreground mt-0.5">
                Expires {new Date(tierGrant.endsAt).toLocaleDateString()}
              </p>
            )}
          </div>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => revokeMutation.mutate(tierGrant.id)}
            disabled={revokeMutation.isPending}
          >
            Revoke
          </Button>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No tier granted.</p>
      )}

      {showForm && (
        <form onSubmit={handleGrant} className="mt-4 flex flex-col gap-3 border-t border-border pt-4">
          <div className="flex gap-2">
            <select
              value={selectedTierId}
              onChange={(e) => setSelectedTierId(e.target.value)}
              required
              className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">Select a tier…</option>
              {tiers.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.displayName} ({t.propertyCap} properties)
                </option>
              ))}
            </select>
            <input
              type="date"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              placeholder="Expiry (optional)"
              className="rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={grantMutation.isPending}>
              {grantMutation.isPending ? 'Granting…' : 'Grant'}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => { setShowForm(false); setError(null); }}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      {(revokeMutation.isError || error) && !showForm && (
        <p className="text-sm text-destructive mt-2">{error ?? 'Action failed.'}</p>
      )}
    </div>
  );
}

function AdminUserDetail() {
  const { userId } = Route.useParams();
  const qc = useQueryClient();

  const { data, isPending, error } = useQuery({
    queryKey: ['admin-user', userId],
    queryFn: () =>
      api.get<{ user: UserDetail }>(`/v1/admin/users/${userId}`).then((r) => r.user),
  });

  const toggleMutation = useMutation({
    mutationFn: (disable: boolean) =>
      api
        .patch<{ user: UserDetail }>(`/v1/admin/users/${userId}`, {
          deletedAt: disable ? new Date().toISOString() : null,
        })
        .then((r) => r.user),
    onSuccess: (updated) => {
      qc.setQueryData(['admin-user', userId], updated);
      void qc.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });

  if (isPending) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (error || !data) return <p className="text-sm text-destructive">Failed to load user.</p>;

  return (
    <div className="max-w-2xl">
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
        <Link to="/admin/users" className="hover:text-foreground">Users</Link>
        <span>/</span>
        <span className="text-foreground">{data.email}</span>
      </div>

      <div className="rounded-lg border border-border p-5 mb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-semibold text-lg">{data.displayName}</div>
            <div className="text-sm text-muted-foreground">{data.email}</div>
            <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
              <span>Joined {new Date(data.createdAt).toLocaleDateString()}</span>
              <span>Email: {data.emailStatus}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {data.deletedAt ? (
              <span className="text-xs bg-red-100 dark:bg-red-950 text-red-700 dark:text-red-300 px-2 py-0.5 rounded">
                Disabled
              </span>
            ) : (
              <span className="text-xs bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300 px-2 py-0.5 rounded">
                Active
              </span>
            )}
            <Button
              variant={data.deletedAt ? 'outline' : 'destructive'}
              size="sm"
              onClick={() => toggleMutation.mutate(!data.deletedAt)}
              disabled={toggleMutation.isPending}
            >
              {data.deletedAt ? 'Enable' : 'Disable'}
            </Button>
          </div>
        </div>
        {toggleMutation.isError && (
          <p className="text-sm text-destructive mt-2">Action failed. Try again.</p>
        )}
      </div>

      <TierSection
        userId={data.id}
        tierGrant={data.tierGrant}
        onUpdate={(updated) => {
          qc.setQueryData(['admin-user', userId], { ...data, tierGrant: updated });
        }}
      />

      <h2 className="font-medium mb-2">Properties</h2>
      {data.properties.length === 0 ? (
        <p className="text-sm text-muted-foreground">No properties.</p>
      ) : (
        <div className="rounded-md border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Property</th>
                <th className="text-left px-4 py-2 font-medium">Role</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.properties.map((p) => (
                <tr key={p.id} className="hover:bg-muted/40">
                  <td className="px-4 py-2">
                    <Link
                      to="/admin/properties/$propertyId"
                      params={{ propertyId: p.id }}
                      className="hover:underline"
                    >
                      {p.name}
                    </Link>
                  </td>
                  <td className="px-4 py-2 capitalize">{p.role}</td>
                  <td className="px-4 py-2">
                    {p.archivedAt ? (
                      <span className="text-xs text-muted-foreground">Archived</span>
                    ) : (
                      <span className="text-xs text-green-600">Active</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
